/**
 * Visual meter showing current token count + status + cost estimate.
 */
import {
  TOKEN_THRESHOLDS,
  tokenStatus,
  estimateCostUsd,
  type TokenStatus,
} from '../../lib/tokenCount.ts';

const STATUS_STYLES: Record<TokenStatus, { bar: string; text: string; label: string }> = {
  ok: { bar: 'bg-pine', text: 'text-pine', label: 'OK' },
  soft_warn: {
    bar: 'bg-yellow-500',
    text: 'text-yellow-700',
    label: 'Long input - transform will be slower',
  },
  cost_warn: {
    bar: 'bg-orange-500',
    text: 'text-orange-700',
    label: 'Very long input - cost will be noticeable',
  },
  hard_cap: {
    bar: 'bg-red-500',
    text: 'text-red-700',
    label: 'Over limit - shorten input before transforming',
  },
};

export function TokenMeter({ tokens }: { tokens: number }) {
  const status = tokenStatus(tokens);
  const style = STATUS_STYLES[status];
  const pct = Math.min(100, (tokens / TOKEN_THRESHOLDS.HARD_CAP) * 100);
  const cost = estimateCostUsd(tokens);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-charcoal">
          {tokens.toLocaleString()} tokens
          <span className="text-smoke">
            {' '}
            · est. ${cost.toFixed(3)}
          </span>
        </span>
        <span className={style.text}>{style.label}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded bg-smoke/20">
        <div
          className={'h-full transition-all ' + style.bar}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-smoke">
        <span>0</span>
        <span>30k</span>
        <span>100k</span>
        <span>180k (limit)</span>
      </div>
    </div>
  );
}
